import React, {useEffect, useLayoutEffect} from 'react';
import {ErrorBoundary} from 'react-error-boundary';
import {useNavigate} from 'react-router';
import {Outlet, useLocation} from 'react-router-dom';
import AnalyticPageView from '@di-shared/features/analytics/AnalyticPageView';
import GlobalErrorBoundary from '@di-shared/features/common/GlobalErrorBoundary';
import useCcWebviewUtilsHelper from '@di-shared/features/common/useCcWebviewUtilsHelper';
import {useEnvironmentStore} from '@di-shared/features/environment/environmentStore';
import useFirebaseStore from '@di-shared/features/environment/firebaseStore';
import getDeviceType from '@di-shared/features/environment/getDeviceType';
import WhitelistAccountBadge from '@di-shared/features/environment/WhitelistAccountBadge';
import {isEventDuration} from '@di-shared/pages/[vin]/capital/event1';
import {ROUTE_PATHS} from '@di-shared/routes';
import {ccWebviewUtils} from '@shared/cci/features/webviewUtils';
import useModal from '@shared/modal/useModal';

function App(): JSX.Element {
  const {closeAllModal} = useModal();
  const location = useLocation();
  const {backOrClose} = useCcWebviewUtilsHelper();
  const navigate = useNavigate();
  const setDevice = useEnvironmentStore((state) => state.setDevice);
  const {isMaintenance, maintenanceMessage, init} = useFirebaseStore(({isMaintenance, maintenanceMessage, init}) => ({
    isMaintenance,
    maintenanceMessage,
    init,
  }));

  useEffect(() => {
    function resizeListener() {
      setDevice(getDeviceType());
    }

    window.addEventListener('resize', resizeListener);

    return () => {
      window.removeEventListener('resize', resizeListener);
    };
  }, [setDevice]);

  useLayoutEffect(() => {
    closeAllModal(true);
  }, [closeAllModal, location.pathname]);

  useLayoutEffect(() => {
    if (import.meta.env.VITE_PROFILE === 'prod') {
      const maintenanceUrl = ROUTE_PATHS.MAINTENANCE.getFullPath(maintenanceMessage);
      if (maintenanceUrl.includes(`${location.pathname}?`)) {
        if (!isMaintenance) {
          // 점검 페이지면서, 점검중이 아닌 경우
          backOrClose();
        }
      } else {
        if (isMaintenance) {
          // 점검 페이지가 아닌데, 점검중인 경우
          navigate(maintenanceUrl);
        }
      }
    }
    // 경우에 따라, navigate 혹은 back 이 n번 수행되는것을 원치 않음. 따라서 의존성 고의로 제거
  }, [isMaintenance, location.pathname, maintenanceMessage]);

  async function openIntroBottomSheet() {
    /**
     * [2025-10-29:Jeyoung Lee] FIXME: 예외적으로 vin을 취득할수 없어서 캐피탈vin 화이트리스트도 이벤트기간이 아니면 이벤트배너를 볼 수 없음
     */
    if (isEventDuration()) {
      return;
    }
    const isConfirm = await ccWebviewUtils.openBottomSheetWebViewUrl(ROUTE_PATHS.BOTTOM_SHEET_INTRODUCE.getFullPath());
    if (isConfirm) {
      navigate(ROUTE_PATHS.ABOUT.getFullPath());
    }
  }

  useEffect(() => {
    const IS_INTRO_PAGE_VIEW = 'isIntroPageView';
    const isIntroPageView = localStorage.getItem(IS_INTRO_PAGE_VIEW);
    if (!isIntroPageView) {
      localStorage.setItem(IS_INTRO_PAGE_VIEW, 'true');
      openIntroBottomSheet();
    }

    init(); // init firebase
  }, []);

  useEffect(() => {
    async function getInitFontSize() {
      const initFontScale = await ccWebviewUtils.getFontScale();
      const htmlFontSize = getComputedStyle(document.documentElement).fontSize; // ex) 10px
      const matchResult = htmlFontSize.trim().match(/^(\d+)([a-zA-Z]+)$/);

      if (!matchResult) {
        // 만약 matchResult가 null이 반환되는경우 font-size 변환하지 않고 바로 return
        return;
      }

      if (initFontScale >= 1.5) {
        document.querySelector('body')?.classList.add('text-scale');
      } else {
        document.querySelector('body')?.classList.remove('text-scale');
      }

      const [_, size, unit] = matchResult;
      document.documentElement.style.setProperty('font-size', `${Number(size) * initFontScale}${unit}`);
    }

    getInitFontSize();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={GlobalErrorBoundary}>
      <AnalyticPageView />
      <WhitelistAccountBadge />
      <Outlet />
    </ErrorBoundary>
  );
}

export default App;
